import { useNavigate, useSearchParams } from 'react-router';
import { Alert, AuthShell, Badge, Button, Stack, Typography } from '@platform/design-system';
import { mockPayPersonalCheckout, safeReturnTo } from '@platform/app-kit';
import type { Runtime } from '../bootstrap.ts';

/**
 * Mock mode only: stands in for the payment page so the whole sign-up can be walked with no
 * backend. "Pay" completes the personal checkout in the mock world (provisioning, then active
 * with the person as owner) and returns to the success path; "Cancel" returns to the cancel path
 * — both exactly as the real payment page would.
 */
export function MockCheckoutPage({ runtime }: { runtime: Runtime }) {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const tenant = params.get('tenant') ?? '';
  const returnTo = safeReturnTo(params.get('return'), '/');
  const cancelTo = safeReturnTo(params.get('cancel'), '/subscribe?cancelled=1');

  return (
    <AuthShell brandName="Mock checkout" pitch="A stand-in for the payment page." title="Pay">
      <Stack spacing={2}>
        <Badge tone="warning" label="Mock session — no backend" />
        <Alert severity="info">
          Monthly plan for account <strong>{tenant}</strong>. Nothing is charged; this page only
          completes the mock sign-up.
        </Alert>
        <Button
          variant="contained"
          size="large"
          onClick={() => {
            if (runtime.world) mockPayPersonalCheckout(runtime.world, tenant);
            void navigate(returnTo, { replace: true });
          }}
        >
          Pay now
        </Button>
        <Button size="large" onClick={() => void navigate(cancelTo, { replace: true })}>
          Cancel
        </Button>
      </Stack>
    </AuthShell>
  );
}

/** Mock mode only: stands in for the billing portal. */
export function MockPortalPage() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const returnTo = safeReturnTo(params.get('return'), '/');
  return (
    <AuthShell brandName="Mock portal" pitch="A stand-in for the billing portal." title="Billing">
      <Stack spacing={2}>
        <Badge tone="warning" label="Mock session — no backend" />
        <Typography variant="body1" color="text.secondary">
          Card details, invoices and cancelling the plan would live here.
        </Typography>
        <Button
          variant="contained"
          size="large"
          onClick={() => void navigate(returnTo, { replace: true })}
        >
          Back to my account
        </Button>
      </Stack>
    </AuthShell>
  );
}
